import React, { useState, useEffect } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Plus,
    MessageSquare,
    Trash2,
    Ellipsis,
    Pencil,
    Heart,
} from 'lucide-react';
import { useTheme } from '../../ThemeContext.tsx';
import { Session } from '../../types/chat.ts';
import AvatarSection from './AvatarSection.tsx';
import InfoTooltip from '../InfoTooltip.tsx';
import hvnLogo from '../../assets/HNV_Logo_light.png';

type SidebarProps = {
    isCollapsed: boolean;
    onToggle: () => void;
    sessions: Session[];
    activeSessionId: string | null;
    onNewChat: () => void;
    onSelectSession: (id: string) => void;
    onDeleteSession: (id: string) => void;
    onToggleFavorite: (id: string, isFavorite: boolean) => void;
    onRenameSession: (id: string, title: string) => void;
    onLogout?: () => void;
    isMobile?: boolean;
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle, sessions, activeSessionId, onNewChat, onSelectSession, onDeleteSession, onToggleFavorite, onRenameSession, onLogout, isMobile = false, isMobileOpen = false, onMobileClose }) => {
    const { colors } = useTheme();
    const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
    const [confirmDeleteFor, setConfirmDeleteFor] = useState<{ id: string; title: string } | null>(null);
    const [showFavorites, setShowFavorites] = useState(true);
    const [showChats, setShowChats] = useState(true);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');

    useEffect(() => {
        if (!menuOpenFor) return;
        const handle = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('#sidebar-ellipsis-menu')) return;
            setMenuOpenFor(null);
        };
        window.addEventListener('click', handle);
        return () => window.removeEventListener('click', handle);
    }, [menuOpenFor]);

    const handleStartRename = (sessionId: string, currentTitle: string) => {
        setRenamingSessionId(sessionId);
        setRenameValue(currentTitle);
        setMenuOpenFor(null);
    };

    const handleRenameSubmit = () => {
        if (renamingSessionId && renameValue.trim()) {
            onRenameSession(renamingSessionId, renameValue.trim());
        }
        setRenamingSessionId(null);
        setRenameValue('');
    };

    const handleRenameCancel = () => {
        setRenamingSessionId(null);
        setRenameValue('');
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleRenameSubmit();
        } else if (e.key === 'Escape') {
            handleRenameCancel();
        }
    };

    // Close sidebar when session is selected on mobile
    const handleSelectSessionMobile = (id: string) => {
        onSelectSession(id);
        if (isMobile && onMobileClose) {
            onMobileClose();
        }
    };

    // Close sidebar on outside click for mobile
    useEffect(() => {
        if (!isMobile || !isMobileOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Don't close if clicking inside sidebar panel, ellipsis menu, or delete modal
            if (isMobileOpen &&
                !target.closest('.sidebar-panel') &&
                !target.closest('#sidebar-ellipsis-menu') &&
                !target.closest('.delete-modal')) {
                onMobileClose?.();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, isMobileOpen, onMobileClose]);

    // Render sidebar content (used for both mobile and desktop)
    const renderSidebarContent = () => (
        <div className={`flex flex-col h-full ${colors.bg.secondary}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3">
                <img src={hvnLogo} alt="Heineken Green Logo" className="h-8 scale-110" />
                {isMobile && onMobileClose && (
                    <button onClick={onMobileClose} className={`p-1 rounded-md ${colors.interactive.secondaryHover}`}>
                        <ChevronLeft size={25} className={colors.text.secondary} />
                    </button>
                )}
            </div>
            {/* New Chat Button */}
            <div className="p-2">
                <button
                    onClick={() => {
                        onNewChat();
                        if (isMobile && onMobileClose) onMobileClose();
                    }}
                    className={`flex w-full items-center justify-start gap-2 p-2 rounded-md ${colors.interactive.primary} ${colors.interactive.primaryHover} ${colors.text.inverse} font-semibold`}
                >
                    <Plus size={20} />
                    <span>New Chat</span>
                </button>
            </div>
            {/* Sessions List */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 hide-scrollbar">
                {/* Favorites section */}
                <div className="px-1 pt-1">
                    <button
                        className={`w-full flex items-center justify-between px-2 py-1 text-xs font-semibold ${colors.text.tertiary}`}
                        onClick={() => setShowFavorites(v => !v)}
                    >
                        <div className="flex items-center gap-2">
                            <span className='text-lg'>Favorites</span>
                            <InfoTooltip text="Pinned your favorite conversations" />
                        </div>
                        {showFavorites ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {showFavorites && (
                        <div className="mt-1">
                            {sessions.filter(s => s.isFavorite).length === 0 ? (
                                <div className={`px-2 py-1 text-xs ${colors.text.tertiary}`}>No favorites yet</div>
                            ) : (
                                sessions.filter(s => s.isFavorite).map(session => (
                                    <div key={session.id} className="relative group my-1">
                                        {renamingSessionId === session.id ? (
                                            <div className="flex items-center gap-2 p-2 w-full">
                                                <input
                                                    type="text"
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={handleRenameKeyDown}
                                                    onBlur={handleRenameSubmit}
                                                    className={`flex-1 ${colors.bg.primary} ${colors.text.primary} border ${colors.border.secondary} rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                    autoFocus
                                                />
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleSelectSessionMobile(session.id)}
                                                className={`flex items-center justify-between gap-2 p-2 w-full text-left rounded-md text-sm ${activeSessionId === session.id
                                                    ? `${colors.bg.tertiary} ${colors.text.primary}`
                                                    : `${colors.interactive.secondaryHover} ${colors.text.secondary}`
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <MessageSquare size={16} className="flex-shrink-0 w-auto" />
                                                    <span className="truncate">{session.title}</span>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        // On mobile, position menu to the left of the button if it would go off-screen
                                                        const menuWidth = 50; // Icon-only menu for mobile
                                                        const wouldOverflow = rect.right + 8 + menuWidth > window.innerWidth;
                                                        const left = wouldOverflow ? rect.left - menuWidth - 8 : rect.right + 8;
                                                        setMenuPos({ top: rect.top - 8, left });
                                                        setMenuOpenFor(prev => prev === session.id ? null : session.id);
                                                    }}
                                                    className={`opacity-0 group-hover:opacity-100 ${colors.text.tertiary} hover:${colors.text.primary} p-1 rounded-md`}
                                                >
                                                    <Ellipsis size={16} />
                                                </button>
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Chats section */}
                <div className="px-1 pt-2">
                    <button
                        className={`w-full flex items-center justify-between px-2 py-1 text-xs font-semibold ${colors.text.tertiary}`}
                        onClick={() => setShowChats(v => !v)}
                    >
                        <div className="flex items-center gap-2">
                            <span className='text-lg'>Chats</span>
                            <InfoTooltip text="Get better answers by including OpCo Name, KPI Name, and Time Period in your questions" />
                        </div>
                        {showChats ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {/* Only render chat items when expanded to reduce initial clutter */}
                    {showChats && (
                        <div className="mt-1">
                            {sessions.filter(s => !s.isFavorite).map(session => (
                                <div key={session.id} className="relative group my-1">
                                    {renamingSessionId === session.id ? (
                                        <div className="flex items-center gap-2 p-2 w-full">
                                            <input
                                                type="text"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onKeyDown={handleRenameKeyDown}
                                                onBlur={handleRenameSubmit}
                                                className={`flex-1 ${colors.bg.primary} ${colors.text.primary} border ${colors.border.secondary} rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                autoFocus
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleSelectSessionMobile(session.id)}
                                            className={`flex items-center justify-between gap-2 p-2 w-full text-left rounded-md text-sm ${activeSessionId === session.id
                                                ? `${colors.bg.tertiary} ${colors.text.primary}`
                                                : `${colors.interactive.secondaryHover} ${colors.text.secondary}`
                                                }`}
                                        >
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <MessageSquare size={16} className="flex-shrink-0 w-auto" />
                                                <span className="truncate">{session.title}</span>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                    // On mobile, position menu to the left of the button if it would go off-screen
                                                    const menuWidth = 50; // Icon-only menu for mobile
                                                    const wouldOverflow = rect.right + 8 + menuWidth > window.innerWidth;
                                                    const left = wouldOverflow ? rect.left - menuWidth - 8 : rect.right + 8;
                                                    setMenuPos({ top: rect.top - 8, left });
                                                    setMenuOpenFor(prev => prev === session.id ? null : session.id);
                                                }}
                                                className={`opacity-0 group-hover:opacity-100 ${colors.text.tertiary} hover:${colors.text.primary} p-1 rounded-md`}
                                            >
                                                <Ellipsis size={16} />
                                            </button>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </nav>

            {/* Divider */}
            <div className={`mx-2 border-t ${colors.border.primary}`} />

            {/* User Dropdown - Bottom of Sidebar */}
            {onLogout && (
                <div className="p-3 border-t border-gray-200 mt-auto">
                    <AvatarSection onLogout={onLogout} compact={false} />
                </div>
            )}
        </div>
    );

    // Mobile sidebar wrapper with overlay
    if (isMobile) {
        return (
            <>
                <div className={`fixed inset-0 z-40 pointer-events-none transition-opacity duration-500 ${isMobileOpen ? 'opacity-100' : 'opacity-0'}`}>
                    <div
                        className={`absolute inset-0 bg-black/20 transition-opacity duration-500 ${isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                        onClick={onMobileClose}
                    />
                    <div
                        className={`sidebar-panel fixed left-0 top-0 h-full w-64 ${colors.bg.secondary} border-r ${colors.border.primary} shadow-xl transform transition-transform duration-500 ease-in-out ${isMobileOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'}`}
                    >
                        {renderSidebarContent()}
                    </div>
                </div>

                {/* Fixed-position dropdown menu for mobile */}
                {menuOpenFor && menuPos && (
                    <div
                        id="sidebar-ellipsis-menu"
                        className={`fixed z-50 ${colors.bg.primary} border ${colors.border.primary} rounded-md shadow-xl p-1 flex flex-col`}
                        style={{ top: menuPos.top, left: menuPos.left }}
                    >
                        {(() => {
                            const s = sessions.find(x => x.id === menuOpenFor);
                            if (!s) return null;
                            return (
                                <>
                                    <button
                                        className={`flex items-center justify-center p-2 rounded-md ${colors.text.primary} ${colors.interactive.secondaryHover}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartRename(s.id, s.title);
                                        }}
                                        title="Rename"
                                    >
                                        <Pencil size={18} />
                                    </button>
                                    <button
                                        className={`flex items-center justify-center p-2 rounded-md transition-all duration-200 ${s.isFavorite
                                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                            : `${colors.text.primary} ${colors.interactive.secondaryHover} hover:bg-red-50 hover:text-red-600`
                                            }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpenFor(null);
                                            onToggleFavorite(s.id, !s.isFavorite);
                                        }}
                                        title={s.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                                    >
                                        <Heart
                                            size={18}
                                            className={`transition-all duration-200 ${s.isFavorite
                                                ? 'text-red-500 fill-red-500 scale-110'
                                                : 'text-gray-400 hover:text-red-500 hover:scale-105'
                                                }`}
                                        />
                                    </button>
                                    <button
                                        className={`flex items-center justify-center p-2 rounded-md hover:bg-red-500/10 hover:text-red-600`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpenFor(null);
                                            // Auto-delete if empty, otherwise show confirmation
                                            if (s.messages.length === 0) {
                                                onDeleteSession(s.id);
                                            } else {
                                                setConfirmDeleteFor({ id: s.id, title: s.title });
                                            }
                                        }}
                                        title="Delete"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* Confirm delete modal for mobile */}
                {confirmDeleteFor && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setConfirmDeleteFor(null)}>
                        <div className="absolute inset-0 bg-black/50" />
                        <div className={`delete-modal relative w-full max-w-sm mx-4 rounded-lg border ${colors.border.primary} ${colors.bg.secondary} p-4`}
                            onClick={(e) => e.stopPropagation()}>
                            <p className={`text-lg font-semibold mb-2 ${colors.text.primary}`}>Delete chat?</p>
                            <p className={`${colors.text.secondary} mb-4`}>This will delete <span className="font-semibold">{confirmDeleteFor.title}</span>.</p>
                            <div className="flex items-center justify-end gap-2">
                                <button className={`px-3 py-1 rounded-md ${colors.text.primary} hover:text-black hover: font-semibold`}
                                    onClick={() => setConfirmDeleteFor(null)}>Cancel</button>
                                <button className={`px-3 py-1 rounded-md ${colors.interactive.primary} ${colors.interactive.primaryHover} ${colors.text.inverse} flex items-center gap-1`}
                                    onClick={() => { onDeleteSession(confirmDeleteFor.id); setConfirmDeleteFor(null); }}>
                                    <Trash2 size={16} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    }

    // Desktop sidebar (existing behavior)
    return (
        <div className={`${colors.bg.secondary} flex flex-col transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-60'}`}>
            {/* Header */}
            <div className={`flex ${isCollapsed ? 'flex-col items-center justify-center py-4' : 'items-center justify-between p-3'}`}>
                {!isCollapsed && <img src={hvnLogo} alt="Heineken Green Logo" className="h-8 scale-110" />
                }
                <button onClick={onToggle} className={`p-1 rounded-md ${colors.interactive.secondaryHover} ${isCollapsed ? 'mb-2' : ''}`}>
                    {isCollapsed ? <ChevronRight size={25} className={colors.text.secondary} /> : <ChevronLeft size={25} className={colors.text.secondary} />}
                </button>
            </div>
            {/* New Chat Button */}
            <div className={isCollapsed ? 'flex flex-col items-center justify-center py-2' : 'p-2'}>
                <button
                    onClick={onNewChat}
                    className={`flex ${isCollapsed ? 'flex-col items-center justify-center h-12 w-12 mx-auto' : 'w-full items-center justify-center sm:justify-start'} gap-2 p-2 rounded-md ${colors.interactive.primary} ${colors.interactive.primaryHover} ${colors.text.inverse} font-semibold`}
                >
                    <Plus size={20} />
                    {!isCollapsed && <span>New Chat</span>}
                </button>
            </div>
            {/* Sessions List */}
            <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${isCollapsed ? 'px-0 flex flex-col items-center hide-scrollbar' : 'px-2 thin-scrollbar'}`}>
                {/* Favorites section */}
                {!isCollapsed && (
                    <div className="px-1 pt-1">
                        <button
                            className={`w-full flex items-center justify-between px-2 py-1 text-xs font-semibold ${colors.text.tertiary}`}
                            onClick={() => setShowFavorites(v => !v)}
                        >
                            <div className="flex items-center gap-2">
                                <span className='text-lg'>Favorites</span>
                                <InfoTooltip text="Pinned your favorite conversations" />
                            </div>
                            {showFavorites ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        {showFavorites && (
                            <div className="mt-1">
                                {sessions.filter(s => s.isFavorite).length === 0 ? (
                                    <div className={`px-2 py-1 text-xs ${colors.text.tertiary}`}>No favorites yet</div>
                                ) : (
                                    sessions.filter(s => s.isFavorite).map(session => (
                                        <div key={session.id} className={`relative group my-1`}>
                                            {renamingSessionId === session.id ? (
                                                <div className="flex items-center gap-2 p-2 w-full">
                                                    <input
                                                        type="text"
                                                        value={renameValue}
                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                        onKeyDown={handleRenameKeyDown}
                                                        onBlur={handleRenameSubmit}
                                                        className={`flex-1 ${colors.bg.primary} ${colors.text.primary} border ${colors.border.secondary} rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                        autoFocus
                                                    />
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => onSelectSession(session.id)}
                                                    className={`flex items-center justify-between gap-2 p-2 w-full text-left rounded-md text-sm ${activeSessionId === session.id
                                                        ? `${colors.bg.tertiary} ${colors.text.primary}`
                                                        : `${colors.interactive.secondaryHover} ${colors.text.secondary}`
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-2 overflow-hidden">
                                                        <MessageSquare size={16} className="flex-shrink-0 w-auto" />
                                                        <span className="truncate">{session.title}</span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                            // Position menu to the left of the button if it would go off-screen
                                                            const menuWidth = 180;
                                                            const wouldOverflow = rect.right + 8 + menuWidth > window.innerWidth;
                                                            const left = wouldOverflow ? rect.left - menuWidth - 8 : rect.right + 8;
                                                            setMenuPos({ top: rect.top - 8, left });
                                                            setMenuOpenFor(prev => prev === session.id ? null : session.id);
                                                        }}
                                                        className={`opacity-0 group-hover:opacity-100 ${colors.text.tertiary} hover:${colors.text.primary} p-1 rounded-md`}
                                                    >
                                                        <Ellipsis size={16} />
                                                    </button>
                                                </button>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Chats section */}
                {!isCollapsed && (
                    <div className="px-1 pt-2">
                        <button
                            className={`w-full flex items-center justify-between px-2 py-1 text-xs font-semibold ${colors.text.tertiary}`}
                            onClick={() => setShowChats(v => !v)}
                        >
                            <div className="flex items-center gap-2">
                                <span className='text-lg'>Chats</span>
                                <InfoTooltip text="Get better answers by including OpCo Name, KPI Name, and Time Period in your questions" />
                            </div>
                            {showChats ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        {/* Only render chat items when expanded to reduce initial clutter */}
                        {showChats && (
                            <div className="mt-1">
                                {sessions.filter(s => !s.isFavorite).map(session => (
                                    <div key={session.id} className={`relative group ${isCollapsed ? 'w-12 mx-auto my-2' : 'my-1'}`}>
                                        {renamingSessionId === session.id ? (
                                            <div className="flex items-center gap-2 p-2 w-full">
                                                <input
                                                    type="text"
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={handleRenameKeyDown}
                                                    onBlur={handleRenameSubmit}
                                                    className={`flex-1 ${colors.bg.primary} ${colors.text.primary} border ${colors.border.secondary} rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                    autoFocus
                                                />
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => onSelectSession(session.id)}
                                                className={`flex ${isCollapsed ? 'flex-col items-center justify-center h-12 w-12' : 'items-center justify-between gap-2 p-2 w-full text-left'} rounded-md text-sm ${activeSessionId === session.id
                                                    ? `${colors.bg.tertiary} ${colors.text.primary}`
                                                    : `${colors.interactive.secondaryHover} ${colors.text.secondary}`
                                                    }`}
                                            >
                                                <div className={`flex ${isCollapsed ? 'flex-col items-center justify-center' : 'items-center gap-2 overflow-hidden'}`}>
                                                    <MessageSquare size={16} className="flex-shrink-0 w-auto" />
                                                    {!isCollapsed && <span className="truncate">{session.title}</span>}
                                                </div>
                                                {!isCollapsed && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                            // Position menu to the left of the button if it would go off-screen
                                                            const menuWidth = 180;
                                                            const wouldOverflow = rect.right + 8 + menuWidth > window.innerWidth;
                                                            const left = wouldOverflow ? rect.left - menuWidth - 8 : rect.right + 8;
                                                            setMenuPos({ top: rect.top - 8, left });
                                                            setMenuOpenFor(prev => prev === session.id ? null : session.id);
                                                        }}
                                                        className={`opacity-0 group-hover:opacity-100 ${colors.text.tertiary} hover:${colors.text.primary} p-1 rounded-md`}
                                                    >
                                                        <Ellipsis size={16} />
                                                    </button>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </nav>

            {/* Divider and Chats header (expanded only) */}
            {!isCollapsed && (
                <div className={`mx-2 border-t ${colors.border.primary}`} />
            )}

            {/* Fixed-position dropdown menu rendered once */}
            {!isCollapsed && menuOpenFor && menuPos && (
                <div
                    id="sidebar-ellipsis-menu"
                    className={`fixed z-50 ${colors.bg.primary} border ${colors.border.primary} rounded-md shadow-xl p-1 flex flex-col min-w-[180px]`}
                    style={{ top: menuPos.top, left: menuPos.left }}
                >
                    {(() => {
                        const s = sessions.find(x => x.id === menuOpenFor);
                        if (!s) return null;
                        return (
                            <>
                                <button
                                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm ${colors.text.primary} ${colors.interactive.secondaryHover}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartRename(s.id, s.title);
                                    }}
                                >
                                    <Pencil size={16} />
                                    <span>Rename</span>
                                </button>
                                <button
                                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm transition-all duration-200 ${s.isFavorite
                                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                        : `${colors.text.primary} ${colors.interactive.secondaryHover} hover:bg-red-50 hover:text-red-600`
                                        }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMenuOpenFor(null);
                                        onToggleFavorite(s.id, !s.isFavorite);
                                    }}
                                >
                                    <Heart
                                        size={16}
                                        className={`transition-all duration-200 ${s.isFavorite
                                            ? 'text-red-500 fill-red-500 scale-110'
                                            : 'text-gray-400 hover:text-red-500 hover:scale-105'
                                            }`}
                                    />
                                    <span>
                                        {s.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                                    </span>
                                </button>
                                <button
                                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm hover:bg-red-500/10 hover:text-red-600`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMenuOpenFor(null);
                                        // Auto-delete if empty, otherwise show confirmation
                                        if (s.messages.length === 0) {
                                            onDeleteSession(s.id);
                                        } else {
                                            setConfirmDeleteFor({ id: s.id, title: s.title });
                                        }
                                    }}
                                >
                                    <Trash2 size={16} />
                                    <span>Delete</span>
                                </button>
                            </>
                        );
                    })()}
                </div>
            )}

            {/* User Dropdown - Bottom of Sidebar */}
            {onLogout && (
                <div className={`p-3 border-t border-gray-200 mt-auto ${isCollapsed ? 'flex items-center justify-center' : ''}`}>
                    <AvatarSection onLogout={onLogout} compact={isCollapsed} />
                </div>
            )}

            {/* Confirm delete modal */}
            {confirmDeleteFor && (
                <div className="fixed inset-0 z-30 flex items-center justify-center" onClick={() => setConfirmDeleteFor(null)}>
                    <div className="absolute inset-0 bg-black/50" />
                    <div className={`delete-modal relative w-full max-w-sm mx-4 rounded-lg border ${colors.border.primary} ${colors.bg.secondary} p-4`}
                        onClick={(e) => e.stopPropagation()}>
                        <p className={`text-lg font-semibold mb-2 ${colors.text.primary}`}>Delete chat?</p>
                        <p className={`${colors.text.secondary} mb-4`}>This will delete <span className="font-semibold">{confirmDeleteFor.title}</span>.</p>
                        <div className="flex items-center justify-end gap-2">
                            <button className={`px-3 py-1 rounded-md ${colors.text.primary} hover:text-black hover: font-semibold`}
                                onClick={() => setConfirmDeleteFor(null)}>Cancel</button>
                            <button className={`px-3 py-1 rounded-md ${colors.interactive.primary} ${colors.interactive.primaryHover} ${colors.text.inverse} flex items-center gap-1`}
                                onClick={() => { onDeleteSession(confirmDeleteFor.id); setConfirmDeleteFor(null); }}>
                                <Trash2 size={16} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Sidebar;

